import React, { lazy, Suspense, useState, useTransition, useEffect } from 'react';
import { Route, Routes, Navigate, useLocation } from 'react-router-dom';
import { OnboardingProvider } from '../onboarding/context/OnboardingContext';
import OnboardingErrorBoundary from '../onboarding/components/OnboardingErrorBoundary';
import OnboardingShell from '../onboarding/components/shared/OnboardingShell';
import { getItem } from '../utility/localStorageControl';
import ProofStatsStep from '../onboarding/steps/ProofStatsStep';

// Lazy-loaded step components

const LandingScreen = lazy(() => import('../onboarding/steps/LandingScreen'));
const QualifyStep = lazy(() => import('../onboarding/steps/QualifyStep'));
const DemoTourStep = lazy(() => import('../onboarding/steps/DemoTourStep'));
const LetsGoStep = lazy(() => import('../onboarding/steps/LetsGoStep'));
const PlanStep = lazy(() => import('../onboarding/steps/PlanStep'));
const CustomizeStep = lazy(() => import('../onboarding/steps/CustomizeStep'));
const AgreementSign = lazy(() => import('../onboarding/steps/AgreementSign'));
const IdVerify = lazy(() => import('../onboarding/steps/IdVerify'));
const DamageHandle = lazy(() => import('../onboarding/steps/DamageHandle'));
const CoverageTiersStep = lazy(() => import('../onboarding/steps/CoverageTiersStep'));
const SocialProofStep = lazy(() => import('../onboarding/steps/SocialProofStep'));
const BookingApplyStep = lazy(() => import('../onboarding/steps/BookingApplyStep'));
const GuidebooksRevealStep = lazy(() => import('../onboarding/steps/GuidebooksRevealStep'));
const ArrivalChoiceStep          = lazy(() => import('../onboarding/steps/ArrivalChoiceStep'));
const ArrivalGuidebookPortalStep    = lazy(() => import('../onboarding/steps/ArrivalGuidebookPortalStep'));
const GuestRegistrationPortalStep       = lazy(() => import('../onboarding/steps/GuestRegistrationPortalStep'));
const GuestRegistrationOverviewStep     = lazy(() => import('../onboarding/steps/GuestRegistrationOverviewStep'));
const GuestRegistrationConfirmationStep = lazy(() => import('../onboarding/steps/GuestRegistrationConfirmationStep'));
const TaskIntroStep = lazy(() => import('../onboarding/steps/TaskIntroStep'));
const TaskDetailStep = lazy(() => import('../onboarding/steps/TaskDetailStep'));
const MessagingIntroStep = lazy(() => import('../onboarding/steps/MessagingIntroStep'));
const MessageTemplatesStep = lazy(() => import('../onboarding/steps/MessageTemplatesStep'));
const SmsRevealStep = lazy(() => import('../onboarding/steps/SmsRevealStep'));
const ConnectPmsStep = lazy(() => import('../onboarding/steps/ConnectPmsStep'));
const GuestRegistrationIntroStep = lazy(() => import('../onboarding/steps/GuestRegistrationIntroStep'));
const DisablePmsMessages = lazy(() => import('../onboarding/steps/DisablePmsMessages'));
const ReviewGuidebookStep = lazy(() => import('../onboarding/steps/ReviewGuidebookStep'));
const CompleteStep = lazy(() => import('../onboarding/steps/CompleteStep'));

const OnboardingProtectedRoute = ({ children }) => {
  const cookieToken = getItem('onboarding_access_token');
  if (!cookieToken) {
    return <Navigate to="/onboarding" replace />;
  }
  return children;
};

// Suspense fallback — minimal cream-background loader

const OnboardingLoader = () => (
  <div className="hg-nav-cold-loader">
    <div className="hg-nav-spinner" />
  </div>
);

const OnboardingRoutes = () => {
  const location = useLocation();
  const [displayLocation, setDisplayLocation] = useState(location);
  const [, startTransition] = useTransition();

  useEffect(() => {
    startTransition(() => {
      setDisplayLocation(location);
    });
  }, [location.pathname, location.search]);

  return (
    <OnboardingProvider>
      {/*
        No key wrapper here — keying the full tree caused the header to animate
        from opacity:0 on every navigation (visible flicker). The enter animation
        is now applied inside OnboardingLayout on .hg-left-panel only, so the
        header renders instantly while the content card slides in.
      */}
      <OnboardingErrorBoundary>
      <Suspense fallback={<OnboardingLoader />}>
        <Routes location={displayLocation}>
          <Route element={<OnboardingShell />}>
          <Route index element={<Navigate to="OB1" replace />} />
          <Route path="OB1" element={<LandingScreen />} />

          <Route path="welcome" element={<Navigate to="/onboarding" replace />} />

          <Route path="OB2" element={<OnboardingProtectedRoute><QualifyStep /></OnboardingProtectedRoute>} />
          <Route path="OB3" element={<OnboardingProtectedRoute><DemoTourStep /></OnboardingProtectedRoute>} />
          <Route path="OB4" element={<OnboardingProtectedRoute><LetsGoStep /></OnboardingProtectedRoute>} />
          <Route path="OB5" element={<OnboardingProtectedRoute><ConnectPmsStep /></OnboardingProtectedRoute>} />
          <Route path="OB6" element={<OnboardingProtectedRoute><PlanStep /></OnboardingProtectedRoute>} />
          <Route path="OB7" element={<OnboardingProtectedRoute><GuestRegistrationIntroStep /></OnboardingProtectedRoute>} />
          <Route path="OB8" element={<OnboardingProtectedRoute><CustomizeStep /></OnboardingProtectedRoute>} />
          <Route path="OB9" element={<OnboardingProtectedRoute><AgreementSign /></OnboardingProtectedRoute>} />
          <Route path="OB10" element={<OnboardingProtectedRoute><IdVerify /></OnboardingProtectedRoute>} />
          <Route path="OB11" element={<OnboardingProtectedRoute><DamageHandle /></OnboardingProtectedRoute>} />
          <Route path="OB12" element={<OnboardingProtectedRoute><CoverageTiersStep /></OnboardingProtectedRoute>} />
          <Route path="OB13" element={<OnboardingProtectedRoute><SocialProofStep /></OnboardingProtectedRoute>} />
          <Route path="OB13B" element={<OnboardingProtectedRoute><ProofStatsStep /></OnboardingProtectedRoute>} />
          <Route path="OB14" element={<OnboardingProtectedRoute><BookingApplyStep /></OnboardingProtectedRoute>} />
          <Route path="OB15" element={<OnboardingProtectedRoute><GuidebooksRevealStep /></OnboardingProtectedRoute>} />
          <Route path="OB16" element={<OnboardingProtectedRoute><ArrivalChoiceStep /></OnboardingProtectedRoute>} />
          <Route path="OB16/:listingSlug" element={<OnboardingProtectedRoute><ArrivalChoiceStep /></OnboardingProtectedRoute>} />
          <Route path="OB17" element={<OnboardingProtectedRoute><ArrivalGuidebookPortalStep /></OnboardingProtectedRoute>} />
          <Route path="OB17B" element={<OnboardingProtectedRoute><GuestRegistrationPortalStep /></OnboardingProtectedRoute>} />
          <Route path="OB18" element={<OnboardingProtectedRoute><ReviewGuidebookStep /></OnboardingProtectedRoute>} />
          <Route path="OB18/:listingSlug" element={<OnboardingProtectedRoute><ReviewGuidebookStep /></OnboardingProtectedRoute>} />
          <Route path="OB19" element={<OnboardingProtectedRoute><GuestRegistrationOverviewStep /></OnboardingProtectedRoute>} />
          <Route path="OB20" element={<OnboardingProtectedRoute><GuestRegistrationConfirmationStep /></OnboardingProtectedRoute>} />
          <Route path="OB21" element={<OnboardingProtectedRoute><TaskIntroStep /></OnboardingProtectedRoute>} />
          <Route path="OB22" element={<OnboardingProtectedRoute><TaskDetailStep /></OnboardingProtectedRoute>} />
          <Route path="OB23" element={<OnboardingProtectedRoute><MessagingIntroStep /></OnboardingProtectedRoute>} />
          <Route path="OB24" element={<OnboardingProtectedRoute><MessageTemplatesStep /></OnboardingProtectedRoute>} />
          <Route path="OB25" element={<OnboardingProtectedRoute><SmsRevealStep /></OnboardingProtectedRoute>} />
          <Route path="OB26" element={<OnboardingProtectedRoute><DisablePmsMessages /></OnboardingProtectedRoute>} />
          <Route path="OB27" element={<OnboardingProtectedRoute><CompleteStep /></OnboardingProtectedRoute>} />

          {/* Catch-all: redirect unknown onboarding sub-paths to landing */}
          <Route path="*" element={<Navigate to="/onboarding" replace />} />
          </Route>
        </Routes>
      </Suspense>
      </OnboardingErrorBoundary>
    </OnboardingProvider>
  );
};

export default OnboardingRoutes;
